import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/request';
import { toast } from './toast';

// 手动同步: 触发 → 每 3 秒轮询状态, 直到不再 running → 提示结果并回调刷新.
// 2026-10-20 从 UgcShareList 抽出, UGC 与 prism 同步状态条共用.

const POLL_MS = 3000;

export interface SyncPollResult {
    /** 还在跑 */
    running: boolean;
    /** 失败时的错误信息 (空 = 成功) */
    error?: string;
}

export function useSyncPolling({
    trigger,
    poll,
    onDone,
}: {
    trigger: () => Promise<unknown>;
    poll: () => Promise<SyncPollResult>;
    onDone: () => void;
}) {
    const [syncing, setSyncing] = useState(false);
    const pollRef = useRef<number | null>(null);

    const stop = useCallback(() => {
        if (pollRef.current) window.clearInterval(pollRef.current);
        pollRef.current = null;
    }, []);

    // 卸载时停掉轮询.
    useEffect(() => stop, [stop]);

    const start = useCallback(async () => {
        try {
            await trigger();
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
            return;
        }
        setSyncing(true);
        toast('同步已开始, 完成后自动刷新');
        stop();
        pollRef.current = window.setInterval(async () => {
            try {
                const r = await poll();
                if (!r.running) {
                    stop();
                    setSyncing(false);
                    toast(r.error ? `同步失败: ${r.error}` : '同步完成');
                    onDone();
                }
            } catch {
                /* 轮询失败下一轮重试 */
            }
        }, POLL_MS);
    }, [trigger, poll, onDone, stop]);

    return { syncing, start };
}
